import { almConfig } from "@/almConfig";
import { AlmConfigurationHandler } from "@/handlers/almConfigurationHandler";
import { createAlmHandler } from "@/handlers/almHandler";
import { SessionsHandler } from "@/handlers/sessionsHandler";
import { captureExceptionWithContext } from "@/sentry";
import { createHandlerContext } from "@/stores/context";

type AlmHandler = ReturnType<typeof createAlmHandler>;

interface AlmUserMapping {
  almHandler: AlmHandler;
  sessionId: string;
  type: string;
  name: string;
  almId?: string;
  almName?: string;
  pipId?: string;
  pipName?: string;
  error?: string;
}

interface MappingsResult {
  mappings: AlmUserMapping[];
  failed: boolean;
}

export async function loadAlmUserMappings(sessionId?: string): Promise<MappingsResult> {
  if (sessionId) {
    const res = await loadMappings([sessionId]);
    if (res.mappings.length === 1) {
      return res;
    }
  }
  try {
    return await loadMappings(await loadActiveSessionIds());
  } catch (e) {
    captureExceptionWithContext(e);
    return { mappings: [], failed: true };
  }
}

async function loadMappings(sessionIds: string[]): Promise<MappingsResult> {
  const mappings: Record<string, AlmUserMapping> = {};
  let failed = false;
  for (const id of sessionIds) {
    await addMappingForSession(mappings, id).catch((e: unknown) => {
      captureExceptionWithContext(e);
      failed = true;
    });
  }
  return { mappings: Object.values(mappings), failed };
}

async function loadActiveSessionIds(): Promise<string[]> {
  const sessions = await new SessionsHandler(createHandlerContext()).getSessions();
  return sessions
    .filter((session: { archived: boolean }) => !session.archived)
    .map((session: { session_id: string }) => session.session_id);
}

async function addMappingForSession(
  mappings: Record<string, AlmUserMapping>,
  sessionId: string,
): Promise<void> {
  const almConfigurationHandler = new AlmConfigurationHandler(createHandlerContext());
  const sessionInfo = await almConfigurationHandler.getAlmConfiguration(sessionId);
  const connectionId = sessionInfo.alm_connection_id;
  if (
    !connectionId ||
    mappings[connectionId] ||
    !almConfig(sessionInfo.alm_connection_type).manualUserMapping
  ) {
    return;
  }
  const almHandler = createAlmHandler(sessionInfo);
  if (await mapsByHand(almHandler)) {
    mappings[connectionId] = {
      almHandler,
      sessionId,
      type: almHandler.name,
      name: await almHandler.getName(),
      ...(await almHandler.getAlmUser()),
    };
  }
}

function mapsByHand(almHandler: AlmHandler): Promise<boolean> {
  return almHandler
    .getUserMappingSettings()
    .then((settings: { manual?: boolean } | undefined) => settings?.manual ?? true)
    .catch(() => true);
}
